import {
  ComplianceProgramStep,
  GetComplianceProgramLoadPreviewQuery,
} from "types/graphql-schema";

export type LoadPreview =
  GetComplianceProgramLoadPreviewQuery["complianceProgramLoadPreview"];
export type Writer = LoadPreview["writers"][number];

/**
 * How long one question takes, until the admin says otherwise. A guess the admin
 * makes, not a figure the backend knows, so it stays on the screen and never
 * reaches the query. Five minutes, because most review questions are a rating
 * with a comment, and writing a comment worth reading takes a while.
 */
export const defaultSecondsPerQuestion = 300;

/** The minutes the admin typed, as seconds, or null when they are not a time. */
export const parseMinutesPerQuestion = (text: string): number | null => {
  const minutes = Number(text);
  // Checked after rounding, so a value under half a second is not read as zero.
  const seconds = Math.round(minutes * 60);
  return text.trim() !== "" && Number.isFinite(minutes) && seconds > 0
    ? seconds
    : null;
};

/**
 * The steps a connected tool saves writing in: every review, peer and upward
 * ones included. Today aggregate_context gives peer and upward reviewers meeting
 * notes only, but they are planned to draw on every integration, so the saving
 * counts them in full already. A survey has no Generate Context, so it is not
 * here.
 */
export const toolSteps: ComplianceProgramStep[] = [
  ComplianceProgramStep.SelfReview,
  ComplianceProgramStep.DownwardReview,
  ComplianceProgramStep.PeerReview,
  ComplianceProgramStep.UpwardReview,
];

/**
 * How much of the writing connecting every work tool would save, until the admin
 * types their own. A guess, shown in a box like the time per question, until
 * there is a measured figure to put there instead.
 */
export const defaultToolSavingsPercent = 50;

/** The percentage the admin typed, or null when it is not one. */
export const parsePercent = (text: string): number | null => {
  const percent = Number(text.replace(/[%\s]/g, ""));
  return text.trim() !== "" &&
    Number.isFinite(percent) &&
    percent > 0 &&
    percent <= 100
    ? percent
    : null;
};

/**
 * What connecting the rest of the tools would take off one step's writing, as a
 * fraction of it. Each category still to connect is worth an equal share of the
 * whole saving, in every step a tool saves writing in (toolSteps).
 */
export const stepSavedFraction = (
  step: ComplianceProgramStep,
  categories: { connected: boolean }[],
  savingsPercent: number,
) =>
  categories.length === 0 || !toolSteps.includes(step)
    ? 0
    : (savingsPercent / 100) *
      (categories.filter((category) => !category.connected).length /
        categories.length);

/** How many questions each review in a step asks. */
const questionsEach = (preview: LoadPreview, step: ComplianceProgramStep) =>
  preview.steps.find((line) => line.step === step)?.questionsEach ?? 0;

/** What one writer writes in each step, and what the tools would save there. */
export const writerSteps = (
  writer: Writer,
  preview: LoadPreview,
  secondsPerQuestion: number,
  savedFraction: (step: ComplianceProgramStep) => number,
) =>
  writer.byStep.map((count) => {
    const today =
      count.reviews * questionsEach(preview, count.step) * secondsPerQuestion;
    return {
      step: count.step,
      reviews: count.reviews,
      today,
      saved: today * savedFraction(count.step),
    };
  });

/** Adds up one figure over step lines. */
const sum = <Line>(lines: Line[], value: (line: Line) => number) =>
  lines.reduce((total, line) => total + value(line), 0);

/**
 * What one writer writes, and what connecting the rest of the tools saves them:
 * their steps added up, so a person's total is always the sum of their steps.
 */
export const writerTime = (
  writer: Writer,
  preview: LoadPreview,
  secondsPerQuestion: number,
  savedFraction: (step: ComplianceProgramStep) => number,
) => {
  const lines = writerSteps(writer, preview, secondsPerQuestion, savedFraction);
  const today = sum(lines, (line) => line.today);
  const saved = sum(lines, (line) => line.saved);
  return { today, saved, after: today - saved };
};

/** What a group writes in one step, across the whole group. */
export type GroupStep = {
  step: ComplianceProgramStep;
  /** How many people in the group write in the step. */
  writers: number;
  /** Reviews written in the step, by everyone in the group together. */
  reviews: number;
  questionsEach: number;
  todaySeconds: number;
  savedSeconds: number;
};

export type WriterGroup = {
  key: "managers" | "employees";
  writers: Writer[];
  /** The steps anybody in the group writes in, in the backend's step order. */
  steps: ComplianceProgramStep[];
  /** Each of those steps, whose times add up to the group's own. */
  byStep: GroupStep[];
  /** Across the whole group. */
  todaySeconds: number;
  savedSeconds: number;
};

/**
 * Everybody who writes, split into the managers, who write a manager review,
 * and everyone else. Managers carry most of a review, so one average across
 * both would hide what each is asked for. Groups nobody is in are left out.
 */
export const writerGroups = (
  preview: LoadPreview,
  secondsPerQuestion: number,
  savedFraction: (step: ComplianceProgramStep) => number,
): WriterGroup[] => {
  const isManager = (writer: Writer) =>
    writer.byStep.some(
      (count) => count.step === ComplianceProgramStep.DownwardReview,
    );
  const group = (key: WriterGroup["key"], writers: Writer[]): WriterGroup => {
    // Every writer's own steps, worked out once, so a group's step adds up the
    // same figures each person's card shows, and the group adds up its steps.
    const lines = writers.flatMap((writer) =>
      writerSteps(writer, preview, secondsPerQuestion, savedFraction),
    );
    const steps = preview.steps
      .map((line) => line.step)
      .filter((step) => lines.some((line) => line.step === step));
    return {
      key,
      writers,
      steps,
      byStep: steps.map((step) => {
        const stepLines = lines.filter((line) => line.step === step);
        return {
          step,
          writers: stepLines.length,
          reviews: sum(stepLines, (line) => line.reviews),
          questionsEach: questionsEach(preview, step),
          todaySeconds: sum(stepLines, (line) => line.today),
          savedSeconds: sum(stepLines, (line) => line.saved),
        };
      }),
      todaySeconds: sum(lines, (line) => line.today),
      savedSeconds: sum(lines, (line) => line.saved),
    };
  };
  return [
    group("managers", preview.writers.filter(isManager)),
    group(
      "employees",
      preview.writers.filter((writer) => !isManager(writer)),
    ),
  ].filter((writerGroup) => writerGroup.writers.length > 0);
};

export const formatCount = (count: number) => count.toLocaleString("en-US");

export const plural = (count: number, one: string, many = `${one}s`) =>
  `${formatCount(count)} ${count === 1 ? one : many}`;

/**
 * A short time, for a saving: "35m", "1.5hr" from an hour up, or "290hr" from
 * ten hours up, where half hours stop mattering. Rounded to five
 * minutes from ten, and to the half hour from an hour, because every figure rests
 * on the minutes per question and "9.17 hours" would claim a precision nobody has.
 */
export const formatMinutes = (seconds: number) => {
  // Nothing to answer is no work, not a little of it.
  if (seconds === 0) {
    return "0m";
  }
  if (seconds < 30) {
    return "<1m";
  }
  const minutes = Math.round(seconds / 60);
  if (minutes < 10) {
    return `${minutes}m`;
  }
  // Rounded once to five minutes, so 58 minutes reads "1hr", never "60m".
  const fiveMinutes = Math.round(seconds / 300) * 5;
  if (fiveMinutes < 60) {
    return `${fiveMinutes}m`;
  }
  return seconds < 36000
    ? formatHalfHours(seconds)
    : `${formatCount(Math.round(seconds / 3600))}hr`;
};

/**
 * A saving per person, to five minutes past an hour too: "1hr 15m" where
 * formatMinutes says "1hr". Times the number of people it has to land near the
 * total beside it, and a half hour each is too coarse for that.
 */
export const formatPersonMinutes = (seconds: number) => {
  const fiveMinutes = Math.round(seconds / 300) * 5;
  if (fiveMinutes < 60 || seconds >= 36000) {
    return formatMinutes(seconds);
  }
  const hours = Math.floor(fiveMinutes / 60);
  const rest = fiveMinutes % 60;
  return rest === 0 ? `${hours}hr` : `${hours}hr ${rest}m`;
};

/** "2hr" or "1.5hr", to the nearest half hour. */
const formatHalfHours = (seconds: number) =>
  `${formatCount(Math.round(seconds / 1800) / 2)}hr`;

/**
 * A time in hours, to the nearest half hour: "~2hr", "~1.5hr". The "~" says it
 * is an estimate. Under a quarter of an hour it would round to nothing, so it
 * reads "<0.5hr" instead.
 */
export const formatHours = (seconds: number) => {
  if (seconds === 0) {
    return "0hr";
  }
  return seconds < 900 ? "<0.5hr" : `~${formatHalfHours(seconds)}`;
};

/** How finely a pair of times is rounded: to the half hour, or five minutes. */
const precisions = [1800, 300, 60];

/**
 * A time and what it could be, rounded together and checked: the gap between
 * the two figures as shown has to say the saving, not hide it. Half hours read
 * best, but "~1.5hr → ~1.5hr" for a 20 minute saving says there is none, so the
 * pair drops to five minutes ("~1hr 35m → ~1hr 15m"), and then to the minute,
 * until the gap shown is within a quarter of the real one. Everything that says
 * the saving uses that gap.
 *
 * A pair under the heading passes `matchHeading`. The heading says the saving
 * on its own (formatPersonMinutes), so the gap must read the same: half hours
 * only when they do, and otherwise the saving to five minutes sets the gap
 * ("~2hr 25m → ~1hr 10m" under "1hr 15m/person", not "~2.5hr → ~1hr").
 */
export const timePair = (
  today: number,
  after: number,
  matchHeading = false,
) => {
  const saving = today - after;
  const round = (seconds: number, precision: number) =>
    Math.round(seconds / precision) * precision;
  const precision = (matchHeading ? [1800] : precisions).find((candidate) => {
    const shown = round(today, candidate) - round(after, candidate);
    // Under half a minute saved, showing the same time twice is the truth.
    return saving < 30
      ? shown === 0
      : shown > 0 &&
          Math.abs(shown - saving) <= saving / 4 &&
          (!matchHeading ||
            formatPersonMinutes(shown) === formatPersonMinutes(saving));
  });
  if (precision !== undefined) {
    return {
      precision,
      today: round(today, precision),
      after: round(after, precision),
    };
  }
  // Even to the minute, both times can round to the same one ("~5m → ~5m"
  // for 40 seconds saved). The saving, rounded on its own, sets the gap: to
  // the minute, or for the heading to five minutes from ten, as it counts.
  const fallback = matchHeading && saving >= 600 ? 300 : 60;
  return {
    precision: fallback,
    today: round(today, fallback),
    after: round(today, fallback) - round(saving, fallback),
  };
};

/**
 * One rounded time of a pair: "~1.5hr" to the half hour, "~1hr 35m" or "~25m"
 * to five minutes or the minute. Nothing reads "0m", and a time that rounds to
 * nothing but is not reads as under the precision.
 */
export const formatEstimate = (
  rounded: number,
  precision: number,
  actual: number,
) => {
  if (actual === 0) {
    return "0m";
  }
  if (rounded === 0) {
    return precision === 1800 ? "<0.5hr" : `<${precision / 60}m`;
  }
  return `~${formatRounded(rounded, precision)}`;
};

/** A time already rounded to a precision: "1.5hr", "1hr 35m" or "25m". */
const formatRounded = (rounded: number, precision: number) => {
  if (precision === 1800) {
    return `${formatCount(rounded / 3600)}hr`;
  }
  const minutes = Math.round(rounded / 60);
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours === 0) {
    return `${rest}m`;
  }
  return rest === 0
    ? `${formatCount(hours)}hr`
    : `${formatCount(hours)}hr ${rest}m`;
};

/**
 * A time and what it could be, as the pair of figures shown side by side, and
 * the saving as the gap between them, at the pair's own precision: rounded
 * again, "~2hr 40m → ~1hr 15m" would save "1.5hr", not "1hr 25m".
 */
export const formatTimePair = (
  today: number,
  after: number,
  matchHeading = false,
) => {
  const pair = timePair(today, after, matchHeading);
  const gap = pair.today - pair.after;
  return {
    today: formatEstimate(pair.today, pair.precision, today),
    after: formatEstimate(pair.after, pair.precision, after),
    saved: gap === 0 ? "0m" : formatRounded(gap, pair.precision),
  };
};
